import { useEffect, useState, useCallback, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';
import { useUpdateUserMutation, useVerifyUserMutation, useConfirmEnrollmentMutation } from '../../data/customer';
import { SLICES } from '../../constants/slices';
import { CSPUrlsMap } from '../../routes/CSPUrlsMap';
import { setOobCode, setTokenClaims, setAuthDetails } from '../../store/slice/authSlice';
import { LOCAL_STORAGE_KEYS } from '../../constants/localStorageKeys';
import useResendTimer from '../../hooks/useResendTimer';
import { API_STATUS_MESSAGE, STATUS_CODE } from '../../constants/verificationStatus';
import { ALERT_ERROR_MESSAGE } from '../../constants/errorMessage';
import { BrandContext } from '../../contextProviders/brandContext';
import useStepupChallenge from '../../hooks/useStepupChallenge';
import { AUTH0_SCOPE } from '../../constants/constants';

const { COUNT_DOWN_END_TIME } = LOCAL_STORAGE_KEYS;

const useVerify = ({ verifyType, verifyTypeInput, verifyCode, skipRedirection = false, isEnabled = true }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { stepUpSmsChallenge } = useStepupChallenge();
  const { getAccessTokenSilently, handleRedirectCallback, isLoading: isAuth0Loading } = useAuth0();
  const { tokenClaims, oobCode, authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { partnerNumber, cspAccessToken, mfaAccessToken } = authDetails;
  const [updateUser, { isLoading: updateUserLoading }] = useUpdateUserMutation();
  const [verifyUser, { isLoading: verifyUserLoading }] = useVerifyUserMutation();
  const [confirmEnrollment, { isLoading: confirmEnrollmentLoading }] = useConfirmEnrollmentMutation();
  const [errorMessage, setErrorMessage] = useState('');
  const savedEndTime = localStorage.getItem(COUNT_DOWN_END_TIME);
  const hasActiveResendTimer = Number(savedEndTime) > Date.now();
  const [isCodeSent, setIsCodeSent] = useState(Boolean(isEnabled && oobCode));
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [isNavigateToSmsVerification, setIsNavigateToSmsVerification] = useState(false);
  const { count: timeLeft, resetTimer, clearTimer } = useResendTimer(isCodeSent);
  const { cspMfaUrl } =
    useContext(BrandContext).activeBrandContext.cspConfiguration.auth0Config[process.env.REACT_APP_ENV];
  const authCallbackParams = new URLSearchParams(window.location.search);
  const hasAuthCallback = authCallbackParams.has('code') && authCallbackParams.has('state');

  useEffect(() => {
    const fetchMfaAccessToken = async () => {
      try {
        if (hasAuthCallback) {
          await handleRedirectCallback();
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        const mfaToken = await getAccessTokenSilently({
          authorizationParams: {
            audience: cspMfaUrl,
            scope: AUTH0_SCOPE
          }
        });

        if (mfaToken) {
          dispatch(
            setAuthDetails({
              ...authDetails,
              mfaAccessToken: mfaToken
            })
          );
        }
      } catch (error) {
        setErrorMessage(ALERT_ERROR_MESSAGE.ERROR_OCCURRED);
      }
    };

    if (!isAuth0Loading && (hasAuthCallback || !mfaAccessToken)) {
      fetchMfaAccessToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleRedirectCallback, getAccessTokenSilently, isAuth0Loading]);

  const getVerifyCode = useCallback(async () => {
    const updateUserBody = {
      Input: verifyTypeInput,
      Type: verifyType,
      UserID: tokenClaims?.sub,
      BrandPartner: partnerNumber,
      Token: ''
    };

    try {
      // Check if user exists in CSP then get verify code
      const updateUserResponse = await updateUser({ auth0Token: mfaAccessToken, data: updateUserBody });
      const cspUser = updateUserResponse && 'data' in updateUserResponse ? updateUserResponse.data : {};
      if (cspUser.isSuccessResponse) {
        if (cspUser.message === API_STATUS_MESSAGE.EMAIL_EXISTS) {
          setIsCodeSent(false);
          setErrorMessage(ALERT_ERROR_MESSAGE.ERROR_OCCURRED);
          return;
        }
        const verifyUserResponse = await verifyUser({
          auth0Token: mfaAccessToken,
          data: { ...updateUserBody, Token: mfaAccessToken }
        });
        const verifyUserData = verifyUserResponse && 'data' in verifyUserResponse ? verifyUserResponse.data : {};
        if (verifyUserData.isSuccessResponse && verifyUserData?.statusCode === STATUS_CODE.SUCCESS) {
          dispatch(setOobCode(verifyUserData?.responseObject?.oob_code));
          setIsCodeSent(true);
          setErrorMessage('');
          resetTimer();
        } else if (verifyUserData?.statusCode === STATUS_CODE.TOO_MANY_CODE_REQUEST_SENT) {
          setIsCodeSent(false);
          const displayErrorMessage =
            verifyType === 'verify-email'
              ? ALERT_ERROR_MESSAGE.TOO_MANY_EMAIL_ATTEMPTS
              : ALERT_ERROR_MESSAGE.TOO_MANY_SMS_ATTEMPTS;
          setErrorMessage(displayErrorMessage);
        }
      }
    } catch (e) {
      setIsCodeSent(false);
      setErrorMessage(ALERT_ERROR_MESSAGE.ERROR_OCCURRED);
    }
  }, [
    tokenClaims,
    mfaAccessToken,
    partnerNumber,
    updateUser,
    verifyUser,
    dispatch,
    verifyType,
    verifyTypeInput,
    resetTimer
  ]);

  const confirmUserEnrollment = async () => {
    if (!oobCode) {
      setErrorMessage(ALERT_ERROR_MESSAGE.INVALID_VERIFICATION_CODE);
      return;
    }
    const confirmEnrollBody = {
      Auth0UserUpdateRequest: {
        BrandPartner: authDetails?.partnerNumber,
        ContactId: authDetails?.contactId,
        Input: verifyTypeInput,
        MfaSkip: tokenClaims?.mfaSkip,
        MobileNo: tokenClaims?.phoneNumber,
        PortalUserToken: cspAccessToken,
        Token: mfaAccessToken,
        Type: verifyType,
        UserID: tokenClaims?.sub
      },
      EnrollRequest: {
        OobBinding: verifyCode.toString(),
        OobCode: oobCode
      }
    };
    try {
      const enrollmentResponse = await confirmEnrollment({ auth0Token: mfaAccessToken, data: confirmEnrollBody });
      const verifyUserData = enrollmentResponse && 'data' in enrollmentResponse ? enrollmentResponse.data : {};
      if (verifyUserData?.isSuccessResponse) {
        setIsCodeVerified(true);
        // If user is verifying email, update store and redirect to verify sms page
        if (verifyType === 'verify-email') {
          dispatch(
            setTokenClaims({
              tokenClaims: { ...tokenClaims, verificationPendingEmail: false }
            })
          );
          dispatch(setOobCode(''));
          clearTimer();
          if (!skipRedirection) {
            stepUpSmsChallenge(`${window.location.origin}${CSPUrlsMap.verifySms.url}`);
            setIsNavigateToSmsVerification(true);
          }
        } else {
          // If user is verifying sms, update store and redirect to dashboard
          dispatch(
            setTokenClaims({
              tokenClaims: {
                ...tokenClaims,
                verificationPendingPhone: false,
                registrationInProgress: false,
                verificationPendingEmail: false
              }
            })
          );
          if (!skipRedirection) {
            navigate(CSPUrlsMap.dashboard.url, { replace: true });
          }
        }
      }

      if (verifyUserData?.message.includes('invalid_code')) {
        setErrorMessage(ALERT_ERROR_MESSAGE.INVALID_VERIFICATION_CODE);
      }
    } catch (e) {
      throw Error('Error enrolling user in CSP', e);
    }
  };

  useEffect(() => {
    const canSendCode = tokenClaims?.sub && verifyTypeInput && partnerNumber && mfaAccessToken;
    if (!hasActiveResendTimer && canSendCode && !isCodeSent && isEnabled) {
      getVerifyCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authDetails?.partnerNumber, mfaAccessToken, isCodeSent, isEnabled]);

  useEffect(() => {
    if (!isEnabled) {
      localStorage.removeItem(COUNT_DOWN_END_TIME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);

  return {
    getVerifyCode,
    confirmUserEnrollment,
    isCodeSent,
    errorMessage,
    updateUserLoading,
    verifyUserLoading,
    confirmEnrollmentLoading,
    timeLeft,
    isCodeVerified,
    isNavigateToSmsVerification,
    setIsNavigateToSmsVerification
  };
};

export default useVerify;

useVerify.propTypes = {
  // verify-email and verify-sms are used for verifying email and phone number respectively
  // email and phone are used for updating email and phone number respectively
  verifyType: PropTypes.oneOf(['verify-email', 'verify-sms', 'email', 'phone']).isRequired,
  verifyTypeInput: PropTypes.string.isRequired,
  verifyCode: PropTypes.string.isRequired,
  skipRedirection: PropTypes.bool,
  isEnabled: PropTypes.bool
};
